import React, { useEffect, useLayoutEffect, useState, useContext } from 'react';
import { useUserEvent } from '../hooks';
import { supabase } from '../supabaseClient';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import ListaTalhoes from './ListaTalhoes';
import ModalCompartilhar from './ModalCompartilhar';
import MapSelectModal from '../components/MapSelectModal';
import PropriedadeOperacionalPreview from './PropriedadeOperacionalPreview';
import { FaArrowLeft, FaArrowRight, FaCrown, FaEdit, FaEye, FaFilter, FaHistory, FaHome, FaLayerGroup, FaLeaf, FaListAlt, FaMapMarkedAlt, FaMapMarkerAlt, FaPencilAlt, FaRulerCombined, FaSearch, FaSeedling, FaSignOutAlt, FaSortAmountDown, FaTimes, FaTrash, FaUserFriends } from 'react-icons/fa';
import { ThemeContext } from '../App';
import { aplicarContextoCultivoAoTalhao, buscarContextosCultivo, contextoCultivoLegado } from '../services/ciclosCultivoService';
import {
  archiveOperationalProperty,
  loadOperationalPropertyStructure,
} from '../services/plotMigrationReviewService';
import { integrateOperationalProperties } from '../services/operationalPropertiesService';
import { resetPageScroll } from '../utils/pageScroll';
import { isActiveCrop } from '../utils/cropLifecycle';
import { loadAuthorizedProperties } from '../services/authorizedPropertiesService';
import { propertyActionAllowed } from '../utils/collaborationAccess';
import '../styles/module-shell.css';
import '../styles/tech-components.css';
import './propriedades-platform.css';

const estadosBrasileiros = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

function useIsMobile(bp = 768) {
  const [v, setV] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const h = () => setV(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return v;
}

export default function ListaPropriedades({ user, onVoltarDashboard, onNavigate, previewMode = false, previewData = null, initialContext = null }) {
  useUserEvent({ userId: user?.id, tela: 'ListaPropriedades', evento: 'page_view', comDuração: true });
  const { darkMode } = useContext(ThemeContext);
  const [propriedades, setPropriedades] = useState(() => previewData?.propriedades || []);
  const [talhoesPorPropriedade, setTalhoesPorPropriedade] = useState(() => previewData?.talhoesPorPropriedade || {});
  const [permissoesPorPropriedade, setPermissoesPorPropriedade] = useState(() => previewData?.permissoes || {}); // NOVO
  const [hoverCards, setHoverCards] = useState({}); // NOVO - rastreia hover de cada card
  const [municipiosFiltrados, setMunicipiosFiltrados] = useState([]);
  const [busca, setBusca] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [ordenacao, setOrdenacao] = useState('nome');
  const [nome, setNome] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [estado, setEstado] = useState('');
  const [area, setArea] = useState('');
  const [proprietario, setProprietario] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [coordenadaX, setCoordenadaX] = useState('');
  const [coordenadaY, setCoordenadaY] = useState('');
  const [codeMuni, setCodeMuni] = useState(null);
  const [editandoId, setEditandoId] = useState(null);
  const [propriedadeSelecionada, setPropriedadeSelecionada] = useState(null);
  const [mostrarCadastro, setMostrarCadastro] = useState(false);
  const [mostrarSugestoesMunicipio, setMostrarSugestoesMunicipio] = useState(false);
  const [confirmarExclusao, setConfirmarExclusao] = useState({ aberto: false, id: null, nome: '' });
  const [modalCompartilhar, setModalCompartilhar] = useState({ aberto: false, propriedade: null });
  const [loadingPropriedades, setLoadingPropriedades] = useState(!previewMode);
  const [showMapPropriedade, setShowMapPropriedade] = useState(false);
  const [erroEstruturaOperacional, setErroEstruturaOperacional] = useState('');
  const [excluindoPropriedade, setExcluindoPropriedade] = useState(false);
  const [erroExclusao, setErroExclusao] = useState('');

  const [salvandoPropriedade, setSalvandoPropriedade] = useState(false);
  const isOnline = useNetworkStatus();
  const isMobile = useIsMobile();

  useLayoutEffect(() => {
    resetPageScroll();
  }, [propriedadeSelecionada?.id]);

  // Busca municípios diretamente no Supabase conforme o usuário digita
  useEffect(() => {
    async function buscarMunicipios() {
      if (previewMode) return;

      if (!municipio || municipio.length < 2) {
        setMunicipiosFiltrados([]);
        setMostrarSugestoesMunicipio(false);
        return;
      }

      // Busca no servidor com filtro
      let query = supabase
        .from('municipios_brazil')
        .select('name_muni, code_state')
        .ilike('name_muni', `${municipio}%`) // Começa com o texto digitado
        .order('name_muni')
        .limit(15); // Limita a 15 resultados

      // Se tiver estado selecionado, filtra também
      if (estado) {
        // Converter sigla para código
        const siglaParaCodigo = {
          'RO': 11, 'AC': 12, 'AM': 13, 'RR': 14, 'PA': 15, 'AP': 16, 'TO': 17,
          'MA': 21, 'PI': 22, 'CE': 23, 'RN': 24, 'PB': 25, 'PE': 26, 'AL': 27, 'SE': 28, 'BA': 29,
          'MG': 31, 'ES': 32, 'RJ': 33, 'SP': 35,
          'PR': 41, 'SC': 42, 'RS': 43,
          'MS': 50, 'MT': 51, 'GO': 52, 'DF': 53
        };
        const codigoEstado = siglaParaCodigo[estado];
        if (codigoEstado) {
          query = query.eq('code_state', codigoEstado);
        }
      }

      const { data, error } = await query;

      if (error) {
        console.log('❌ Erro ao buscar municípios:', error);
        return;
      }

      if (data && data.length > 0) {
        console.log('🔍 Autocomplete:', { digitado: municipio, encontrados: data.length });
        console.log('📋 Primeiro resultado (debug):', data[0]);
        
        // Mapear sigla do estado baseado no código
        const codigoParaSigla = {
          11: 'RO', 12: 'AC', 13: 'AM', 14: 'RR', 15: 'PA', 16: 'AP', 17: 'TO',
          21: 'MA', 22: 'PI', 23: 'CE', 24: 'RN', 25: 'PB', 26: 'PE', 27: 'AL', 28: 'SE', 29: 'BA',
          31: 'MG', 32: 'ES', 33: 'RJ', 35: 'SP',
          41: 'PR', 42: 'SC', 43: 'RS',
          50: 'MS', 51: 'MT', 52: 'GO', 53: 'DF'
        };
        
        const municipiosFormatados = data.map(m => ({
          nome_municipio: m.name_muni,
          sigla_estado: codigoParaSigla[m.code_state] || m.code_state
        }));
        setMunicipiosFiltrados(municipiosFormatados);
        setMostrarSugestoesMunicipio(municipiosFormatados.length > 0);
      }
    }

    // Delay para não fazer requisição a cada letra (debounce)
    const timer = setTimeout(buscarMunicipios, 300);
    return () => clearTimeout(timer);
  }, [municipio, estado, previewMode]);

  // Carrega propriedades do usuário + compartilhadas (reutilizável)
  const carregarPropriedades = React.useCallback(async () => {
    if (previewMode) {
      setPropriedades(previewData?.propriedades || []);
      setTalhoesPorPropriedade(previewData?.talhoesPorPropriedade || {});
      setPermissoesPorPropriedade(previewData?.permissoes || {});
      setLoadingPropriedades(false);
      return;
    }
    if (!user?.id) return;
    setLoadingPropriedades(true);
    setErroEstruturaOperacional('');

    // Carrega cache imediatamente para exibir dados offline
    const CACHE_KEY_PROPS = `climorax_props_${user.id}`;
    const CACHE_KEY_PERMS = `climorax_perms_${user.id}`;
    try {
      const cachedProps = localStorage.getItem(CACHE_KEY_PROPS);
      const cachedPerms = localStorage.getItem(CACHE_KEY_PERMS);
      if (cachedProps) {
        const parsedProps = JSON.parse(cachedProps);
        const cacheJaFiltrado = parsedProps.every(property => Boolean(property._operationalOrigin));
        if (cacheJaFiltrado) setPropriedades(parsedProps);
      }
      if (cachedPerms) setPermissoesPorPropriedade(JSON.parse(cachedPerms));
    } catch {}

    try {
      const { properties: authorizedProperties } = await loadAuthorizedProperties(user.id, '*', 'propriedades');
      const permissoes = {};
      authorizedProperties.forEach(property => { permissoes[property.id] = property._accessRole || 'viewer'; });

      let propriedadesOperacionais;
      try {
        const estrutura = await loadOperationalPropertyStructure();
        propriedadesOperacionais = integrateOperationalProperties(authorizedProperties, estrutura);
      } catch (operationalError) {
        setErroEstruturaOperacional(
          operationalError?.message || 'Não foi possível carregar a estrutura atual das propriedades.'
        );
        return;
      }

      propriedadesOperacionais.sort((a, b) => a.nome.localeCompare(b.nome));
      setPropriedades(propriedadesOperacionais);
      setPermissoesPorPropriedade(permissoes);

      // Salva no cache para uso offline
      try {
        localStorage.setItem(CACHE_KEY_PROPS, JSON.stringify(propriedadesOperacionais));
        localStorage.setItem(CACHE_KEY_PERMS, JSON.stringify(permissoes));
      } catch {}

    } catch (err) {
      console.error('Erro ao carregar propriedades:', err);
      // Mantém os dados do cache (já carregados acima)
    } finally {
      setLoadingPropriedades(false);
    }
  }, [user, previewMode, previewData]);

  useEffect(() => {
    carregarPropriedades();
  }, [carregarPropriedades]);

  useEffect(() => {
    if (!initialContext) return;
    const propriedadeAlvo = propriedades.find(item => (
      (initialContext.propertyId && String(item.id) === String(initialContext.propertyId))
      || (initialContext.propertyName && item.nome === initialContext.propertyName)
    ));
    if (propriedadeAlvo) {
      setPropriedadeSelecionada(propriedadeAlvo);
      return;
    }

    const snapshot = initialContext.propertySnapshot;
    if (!snapshot || (!initialContext.propertyId && !snapshot.id)) return;

    const propertyId = initialContext.propertyId || snapshot.id;
    const contextPlots = (Array.isArray(initialContext.propertyPlots)
      ? initialContext.propertyPlots
      : [initialContext.plotSnapshot]
    ).filter(Boolean);
    const propriedadeContextual = {
      ...snapshot,
      id: propertyId,
      _operationalOrigin: snapshot._operationalOrigin || 'central_operacoes',
      _usesPhysicalPlots: true,
      _operationalPlots: contextPlots,
    };

    setTalhoesPorPropriedade(current => ({
      ...current,
      [propertyId]: contextPlots,
    }));
    setPermissoesPorPropriedade(current => ({
      ...current,
      [propertyId]: snapshot._accessRole || current[propertyId] || 'viewer',
    }));
    setPropriedadeSelecionada(propriedadeContextual);
  }, [initialContext, propriedades]);

  // Carrega talhões de todas as propriedades em uma única query
  useEffect(() => {
    async function fetchTalhoes() {
      if (!user?.id || propriedades.length === 0) return;

      const CACHE_KEY_TALHOES = `climorax_talhoes_${user.id}`;

      // Carrega cache imediatamente
      try {
        const cached = localStorage.getItem(CACHE_KEY_TALHOES);
        if (cached) setTalhoesPorPropriedade(JSON.parse(cached));
      } catch {}

      try {
        const propriedadesLegadas = propriedades.filter(p => !p._usesPhysicalPlots);
        const ids = propriedadesLegadas.map(p => p.id);
        let data = [];
        if (ids.length > 0) {
          const result = await supabase
            .from('talhoes')
            .select('id, nome, cultura, area, data_plantio, data_colheita, propriedade_id')
            .in('propriedade_id', ids);
          if (result.error) throw result.error;
          data = result.data || [];
        }

        let contextos = new Map();
        try {
          contextos = await buscarContextosCultivo((data || []).map(t => t.id));
        } catch (cycleError) {
          console.warn('Ciclos de cultivo indisponíveis no resumo; usando compatibilidade local.', cycleError);
        }
        const talhoesComCultivo = (data || []).map(t => aplicarContextoCultivoAoTalhao(
          t,
          contextos.get(String(t.id)) || contextoCultivoLegado(t)
        ));
        const talhoesMap = {};
        propriedades.forEach(p => {
          talhoesMap[p.id] = p._usesPhysicalPlots ? (p._operationalPlots || []) : [];
        });
        talhoesComCultivo.forEach(t => {
          if (talhoesMap[t.propriedade_id]) talhoesMap[t.propriedade_id].push(t);
        });
        setTalhoesPorPropriedade(talhoesMap);

        // Salva no cache
        try { localStorage.setItem(CACHE_KEY_TALHOES, JSON.stringify(talhoesMap)); } catch {}
      } catch (err) {
        console.error('Erro ao carregar talhões:', err);
        // Mantém cache já carregado
      }
    }
    fetchTalhoes();
  }, [user, propriedades]);

  // A criação de propriedades acontece exclusivamente na Central de Operações.
  // Este formulário permanece somente para manutenção de cadastros existentes.
  async function handleCadastrar(e) {
    e.preventDefault();
    if (previewMode) return;
    if (!editandoId) {
      alert('Novas propriedades devem ser cadastradas pela Central de Operações.');
      setMostrarCadastro(false);
      return;
    }
    console.log('handleCadastrar chamado', { nome, municipio, estado, user });
    if (!nome || !municipio || !estado) {
      alert('Preencha todos os campos obrigatórios!');
      return;
    }
    if (!area || isNaN(Number(area)) || Number(area) <= 0) {
      alert('Informe a área total da propriedade (em hectares).');
      return;
    }
    if (!user || !user.id) {
      alert('Usuário não identificado. Faça login novamente.');
      console.error('Usuário não identificado:', user);
      return;
    }
    if (salvandoPropriedade) return; // evita duplo envio
    setSalvandoPropriedade(true);
    try {

    const areaNumber = area ? Number(area) : null;

    const { error } = await supabase
      .from('propriedades')
      .update({
        nome,
        municipio,
        estado,
        area: areaNumber,
        proprietario,
        observacoes,
        coordenada_x: coordenadaX ? parseFloat(coordenadaX) : null,
        coordenada_y: coordenadaY ? parseFloat(coordenadaY) : null,
        code_muni: codeMuni
      })
      .eq('id', editandoId)
      .select();

    if (error) {
      alert('Erro ao editar propriedade!');
      return;
    }

    // Reaplica o filtro da arquitetura nova após a edição.
    await carregarPropriedades();
    setEditandoId(null);
    setNome('');
    setMunicipio('');
    setEstado('');
    setArea('');
    setProprietario('');
    setObservacoes('');
    setCoordenadaX('');
    setCoordenadaY('');
    setCodeMuni(null);
    setMostrarCadastro(false);
    } finally {
      setSalvandoPropriedade(false);
    }
  }

  // Arquiva a propriedade somente na arquitetura nova. O legado permanece intacto.
  async function handleExcluir(id) {
    if (previewMode || excluindoPropriedade) return;
    setExcluindoPropriedade(true);
    setErroExclusao('');
    try {
      await archiveOperationalProperty(id);
      setPropriedades(current => {
        const next = current.filter(p => String(p.id) !== String(id));
        try { localStorage.setItem(`climorax_props_${user.id}`, JSON.stringify(next)); } catch {}
        return next;
      });
      setTalhoesPorPropriedade(current => {
        const next = { ...current };
        delete next[id];
        try { localStorage.setItem(`climorax_talhoes_${user.id}`, JSON.stringify(next)); } catch {}
        return next;
      });
      if (editandoId === id) cancelarEdicaoOuCadastro();
      setConfirmarExclusao({ aberto: false, id: null, nome: '' });
    } catch (error) {
      setErroExclusao(error?.message || 'Não foi possível remover esta propriedade da estrutura nova.');
    } finally {
      setExcluindoPropriedade(false);
    }
  }

  // Remover compartilhamento (sair da propriedade compartilhada)
  async function handleRemoverCompartilhamento(propriedadeId) {
    if (propriedades.find(p => p.id === propriedadeId)?._collaborationInviteIds?.length) {
      onNavigate?.('colaboracao');
      return;
    }
    if (!window.confirm('Deseja sair desta propriedade compartilhada?')) return;
    
    const { error } = await supabase
      .from('propriedades_compartilhadas')
      .delete()
      .eq('propriedade_id', propriedadeId)
      .eq('user_id', user.id);
    
    if (error) {
      alert('Erro ao remover compartilhamento');
      console.error(error);
      return;
    }
    
    // Remover da lista local
    setPropriedades(propriedades.filter(p => p.id !== propriedadeId));
    alert('Você saiu da propriedade compartilhada');
  }

  // Inicia o processo de edição de uma propriedade
  function iniciarEdicao(propriedade) {
    if (!propertyActionAllowed(propriedade, 'propriedades', 'edit', true)) return;
    setEditandoId(propriedade.id);
    setNome(propriedade.nome);
    setMunicipio(propriedade.municipio);
    setEstado(propriedade.estado);
    setArea(propriedade.area);
    setProprietario(propriedade.proprietario || '');
    setObservacoes(propriedade.observacoes || '');
    setCoordenadaX(propriedade.coordenada_x || '');
    setCoordenadaY(propriedade.coordenada_y || '');
    setMostrarCadastro(true);
  }

  // Cancela a edição atual
  function cancelarEdicaoOuCadastro() {
    setEditandoId(null);
    setNome('');
    setMunicipio('');
    setEstado('');
    setArea('');
    setProprietario('');
    setObservacoes('');
    setCoordenadaX('');
    setCoordenadaY('');
    setMostrarCadastro(false);
    setMostrarSugestoesMunicipio(false);
  }

  // Seleciona um município da lista
  async function selecionarMunicipio(mun) {
    console.log('🎯 Município selecionado:', mun);
    setMunicipio(mun.nome_municipio);
    setEstado(mun.sigla_estado);
    setMostrarSugestoesMunicipio(false);
    
    // Buscar code_muni do município selecionado
    try {
      const siglaParaCodigo = {
        'RO': 11, 'AC': 12, 'AM': 13, 'RR': 14, 'PA': 15, 'AP': 16, 'TO': 17,
        'MA': 21, 'PI': 22, 'CE': 23, 'RN': 24, 'PB': 25, 'PE': 26, 'AL': 27, 'SE': 28, 'BA': 29,
        'MG': 31, 'ES': 32, 'RJ': 33, 'SP': 35,
        'PR': 41, 'SC': 42, 'RS': 43,
        'MS': 50, 'MT': 51, 'GO': 52, 'DF': 53
      };
      
      const codeState = siglaParaCodigo[mun.sigla_estado];
      
      const { data, error } = await supabase
        .from('municipios_brazil')
        .select('code_muni')
        .eq('name_muni', mun.nome_municipio)
        .eq('code_state', codeState)
        .single();
      
      if (!error && data?.code_muni) {
        console.log('✅ Code_muni encontrado:', data.code_muni);
        setCodeMuni(data.code_muni);
      } else {
        console.log('⚠️ Code_muni não encontrado');
        setCodeMuni(null);
      }
    } catch (error) {
      console.error('Erro ao buscar code_muni:', error);
      setCodeMuni(null);
    }
  }

  function pedirConfirmacaoExclusao(id, nome) {
    setErroExclusao('');
    setConfirmarExclusao({ aberto: true, id, nome });
  }

  function fecharConfirmacao() {
    if (excluindoPropriedade) return;
    setErroExclusao('');
    setConfirmarExclusao({ aberto: false, id: null, nome: '' });
  }

  // Quem entrou vindo da Central de Operações volta direto para lá (mesma
  // fazenda e visão de mapa) em vez de ficar preso nesta aba.
  function voltarParaOrigem() {
    if (initialContext?.returnTarget && onNavigate) {
      onNavigate(initialContext.returnTarget, initialContext.returnContext || {});
      return true;
    }
    return false;
  }

  // Filtrar e ordenar propriedades
  const propriedadesFiltradas = propriedades
    .filter(p => {
      const matchBusca = !busca || 
        p.nome?.toLowerCase().includes(busca.toLowerCase()) ||
        p.municipio?.toLowerCase().includes(busca.toLowerCase()) ||
        p.proprietario?.toLowerCase().includes(busca.toLowerCase());
      const matchEstado = !filtroEstado || p.estado === filtroEstado;
      return matchBusca && matchEstado;
    })
    .sort((a, b) => {
      if (ordenacao === 'nome') return a.nome.localeCompare(b.nome);
      if (ordenacao === 'area') return (b.area || 0) - (a.area || 0);
      if (ordenacao === 'talhoes') {
        const talhoesA = talhoesPorPropriedade[a.id]?.length || 0;
        const talhoesB = talhoesPorPropriedade[b.id]?.length || 0;
        return talhoesB - talhoesA;
      }
      return 0;
    });

  // Estatísticas gerais
  const stats = {
    totalPropriedades: propriedades.length,
    areaTotal: propriedades.reduce((acc, p) => acc + (p.area || 0), 0),
    totalTalhoes: Object.values(talhoesPorPropriedade).reduce((acc, t) => acc + t.length, 0),
    talhoesAtivos: Object.values(talhoesPorPropriedade).flat().filter(t => isActiveCrop(t.ciclo_atual || t)).length,
    culturas: [...new Set(
      Object.values(talhoesPorPropriedade).flat()
        .map(t => t.cultura)
        .filter(Boolean)
        .map(c => c.trim())
        .map(c => c.charAt(0).toUpperCase() + c.slice(1).toLowerCase())
    )]
  };

  if (propriedadeSelecionada) {
    if (previewMode) {
      return (
        <PropriedadeOperacionalPreview
          propriedade={propriedadeSelecionada}
          talhoes={talhoesPorPropriedade[propriedadeSelecionada.id] || []}
          onBack={() => { if (!voltarParaOrigem()) setPropriedadeSelecionada(null); }}
          onNavigate={onNavigate}
          initialPlotId={initialContext?.plotId}
          initialPlotName={initialContext?.plotName}
        />
      );
    }
    const permissao = permissoesPorPropriedade[propriedadeSelecionada.id] || 'owner';
    return (
      <ListaTalhoes
        propriedade={propriedadeSelecionada}
        initialTalhoes={talhoesPorPropriedade[propriedadeSelecionada.id] || []}
        operationalMode={propriedadeSelecionada._usesPhysicalPlots === true}
        user={user}
        permission={permissao}
        onNavigate={onNavigate}
        returnTarget={initialContext?.returnTarget || null}
        returnContext={initialContext?.returnContext || null}
        onTalhoesChange={updatedTalhoes => setTalhoesPorPropriedade(current => ({
          ...current,
          [propriedadeSelecionada.id]: updatedTalhoes,
        }))}
        onVoltar={() => { if (!voltarParaOrigem()) setPropriedadeSelecionada(null); }}
        onSelecionar={() => {}}
        initialPlotId={initialContext?.plotId}
        initialPlotName={initialContext?.plotName}
        initialSection={initialContext?.section}
      />
    );
  }

  return (
    <div className={`module-shell module-shell-propriedades props-platform-shell ${darkMode ? 'is-dark' : ''}`}>
      {/* Header */}
      <div className="module-topbar props-page-topbar">
        <div className="props-page-header">
          {/* Back button */}
          {onVoltarDashboard && (
            <button
              className="props-page-back"
              onClick={mostrarCadastro ? () => setMostrarCadastro(false) : () => { if (!voltarParaOrigem()) onVoltarDashboard(); }}
              aria-label={mostrarCadastro ? 'Voltar para propriedades' : 'Voltar ao painel'}
            >
              <FaArrowLeft aria-hidden="true" />
              {!isMobile && <span>Voltar</span>}
            </button>
          )}

          {/* Divider */}
          {onVoltarDashboard && <div className="props-page-divider" />}

          {/* Icon + title */}
          <div className="props-page-identity">
            <div className="props-page-icon"><FaMapMarkedAlt aria-hidden="true" /></div>
            <div className="props-page-copy">
              <span className="props-page-eyebrow">Gestão territorial</span>
              <h1 className="module-title props-page-title">
                {mostrarCadastro ? 'Editar propriedade' : 'Propriedades e Talhões'}
              </h1>
              <p className="props-page-subtitle">
                {mostrarCadastro ? 'Mantenha os dados territoriais atualizados' : 'Organize propriedades, áreas produtivas e culturas em um só lugar'}
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* Content wrapper */}
      <div className="props-content-wrap">

        {erroEstruturaOperacional && (
          <div role="alert" style={{
            margin: '8px 0 14px',
            padding: '13px 15px',
            border: '1px solid #efc36f',
            borderRadius: 12,
            color: darkMode ? '#ffe0a3' : '#815311',
            background: darkMode ? '#3a2c16' : '#fff8e9',
            fontSize: 14,
          }}>
            <strong style={{ display: 'block', marginBottom: 4 }}>Não foi possível conferir a estrutura nova</strong>
            <span>{erroEstruturaOperacional}</span>
            <button
              type="button"
              onClick={carregarPropriedades}
              style={{ marginLeft: 10, border: 0, borderRadius: 8, padding: '7px 11px', color: '#fff', background: '#a56c13', fontWeight: 700, cursor: 'pointer' }}
            >
              Tentar novamente
            </button>
          </div>
        )}

        {/* Loading skeleton */}
        {loadingPropriedades && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{
                background: darkMode ? '#232526' : '#fff',
                borderRadius: 12,
                padding: 16,
                boxShadow: darkMode ? '0 2px 8px #0006' : '0 1px 4px #0001',
                animation: 'pulse 1.4s ease-in-out infinite'
              }}>
                <div style={{ height: 16, background: '#e0e0e0', borderRadius: 6, width: '60%', marginBottom: 10 }} />
                <div style={{ height: 12, background: '#f0f0f0', borderRadius: 6, width: '40%', marginBottom: 8 }} />
                <div style={{ height: 12, background: '#f0f0f0', borderRadius: 6, width: '30%' }} />
              </div>
            ))}
            <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
          </div>
        )}

        {/* Dashboard de Estatísticas */}
        {!mostrarCadastro && !loadingPropriedades && propriedades.length > 0 && (
          <section className="props-overview" aria-labelledby="props-overview-title">
            <div className="props-overview-heading">
              <div>
                <span>Visão consolidada</span>
                <h2 id="props-overview-title">Resumo da operação</h2>
              </div>
              <p>Dados reais das propriedades e talhões cadastrados nesta conta.</p>
            </div>
            <div className="props-metrics-grid">
              <article className="props-metric-card is-blue">
                <span className="props-metric-icon"><FaHome /></span>
                <div><small>Propriedades</small><strong>{stats.totalPropriedades}</strong><em>unidades cadastradas</em></div>
              </article>
              <article className="props-metric-card is-cyan">
                <span className="props-metric-icon"><FaRulerCombined /></span>
                <div><small>Área total</small><strong>{stats.areaTotal % 1 === 0 ? stats.areaTotal : stats.areaTotal.toFixed(1)} <b>ha</b></strong><em>território gerenciado</em></div>
              </article>
              <article className="props-metric-card is-green">
                <span className="props-metric-icon"><FaLayerGroup /></span>
                <div><small>Talhões</small><strong>{stats.totalTalhoes}</strong><em>áreas produtivas</em></div>
              </article>
              <article className="props-metric-card is-amber">
                <span className="props-metric-icon"><FaSeedling /></span>
                <div><small>Em cultivo</small><strong>{stats.talhoesAtivos}</strong><em>talhões ativos</em></div>
              </article>
            </div>
            {stats.culturas.length > 0 && (
              <div className="props-cultures-row">
                <span><FaLeaf aria-hidden="true" /> Culturas presentes</span>
                {stats.culturas.map(c => (
                  <b key={c}>{c}</b>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Busca e Filtros */}
        {!mostrarCadastro && !loadingPropriedades && propriedades.length > 0 && (
          <div className="props-filter-panel">
            <label className="props-search-field">
              <FaSearch aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nome, município ou proprietário"
                value={busca}
                onChange={e => setBusca(e.target.value)}
              />
            </label>
            <div className="props-filter-row">
              <label><FaFilter aria-hidden="true" />
              <select
                aria-label="Filtrar por estado"
                className="tech-select"
                value={filtroEstado}
                onChange={e => setFiltroEstado(e.target.value)}
              >
                <option value="">Todos os estados</option>
                {[...new Set(propriedades.map(p => p.estado))].sort().map(e => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
              </label>
              <label><FaSortAmountDown aria-hidden="true" />
              <select
                aria-label="Ordenar propriedades"
                className="tech-select"
                value={ordenacao}
                onChange={e => setOrdenacao(e.target.value)}
              >
                <option value="nome">Nome A-Z</option>
                <option value="area">Maior Área</option>
                <option value="talhoes">Mais Talhões</option>
              </select>
              </label>
            </div>
            {(busca || filtroEstado) && (
              <div className="props-filter-result">
                {propriedadesFiltradas.length} de {propriedades.length} propriedades
              </div>
            )}
          </div>
        )}

        {/* Formulário exclusivo para edição de propriedades existentes */}
        {mostrarCadastro ? (
          <>
            <div className="tech-panel props-form-panel" style={{
              borderRadius: 12,
              padding: 16,
              marginBottom: 24
            }}>
              <h3 className="props-form-title" style={{ color: darkMode ? '#90caf9' : '#1976d2', marginBottom: 12, fontWeight: 600, fontSize: 16 }}>
                Editar propriedade
              </h3>
              <form
                className="props-property-form"
                onSubmit={handleCadastrar}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10
                }}
              >
                <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Nome da Propriedade" required style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #b0c4de', fontSize: 16, boxSizing: 'border-box', background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#e0e0e0' : '#333' }} />
                
                {/* Campo de município com autocomplete */}
                <div style={{ position: 'relative', width: '100%' }}>
                  <input 
                    value={municipio} 
                    onChange={e => setMunicipio(e.target.value)}
                    placeholder="Digite o município (mín. 2 letras)" 
                    required 
                    style={{ 
                      width: '100%', 
                      padding: 10, 
                      paddingRight: municipiosFiltrados.length > 0 ? 60 : 10,
                      borderRadius: 8, 
                      border: mostrarSugestoesMunicipio ? '2px solid #1976d2' : '1px solid #b0c4de', 
                      fontSize: 16,
                      boxSizing: 'border-box'
                    }} 
                    autoComplete="off"
                  />
                  {/* Indicador de resultados */}
                  {municipio.length >= 2 && (
                    <div style={{
                      position: 'absolute',
                      right: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: municipiosFiltrados.length > 0 ? '#4caf50' : '#ff9800',
                      color: '#fff',
                      padding: '4px 8px',
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: 600,
                      pointerEvents: 'none'
                    }}>
                      {municipiosFiltrados.length > 0 ? `${municipiosFiltrados.length}` : '0'}
                    </div>
                  )}
                  {mostrarSugestoesMunicipio && municipiosFiltrados.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: darkMode ? '#232526' : '#fff',
                      border: '1px solid #1976d2',
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      maxHeight: 200,
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
                    }}>
                      {municipiosFiltrados.map(m => (
                        <div
                          key={`${m.nome_municipio}-${m.sigla_estado}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            selecionarMunicipio(m);
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            selecionarMunicipio(m);
                          }}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: `1px solid ${darkMode ? '#444' : '#f0f0f0'}`,
                            fontSize: 15,
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = darkMode ? '#1e3a5f' : '#e3f2fd'}
                          onMouseLeave={(e) => e.currentTarget.style.background = darkMode ? '#232526' : '#fff'}
                        >
                          <div style={{ fontWeight: 500, color: darkMode ? '#e0e0e0' : '#333' }}>{m.nome_municipio}</div>
                          <div style={{ fontSize: 13, color: darkMode ? '#aaa' : '#666', marginTop: 2 }}>{m.sigla_estado}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Select de Estado */}
                <select 
                  value={estado} 
                  onChange={e => setEstado(e.target.value)} 
                  required 
                  style={{ 
                    width: '100%', 
                    padding: 10, 
                    borderRadius: 8, 
                    border: '1px solid #b0c4de', 
                    fontSize: 16,
                    background: darkMode ? '#2a2a2a' : '#fff',
                    color: darkMode ? '#e0e0e0' : '#333',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Selecione o Estado</option>
                  {estadosBrasileiros.map(uf => (
                    <option key={uf} value={uf}>{uf}</option>
                  ))}
                </select>

                {/* Coordenadas geográficas */}
                <div style={{ width: '100%', boxSizing: 'border-box' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, marginLeft: 2 }}>
                    <label style={{ fontSize: 12, color: '#1976d2', fontWeight: 600 }}>
                      Coordenadas <span style={{ fontWeight: 400, color: '#888', fontSize: 11 }}>(opcional)</span>
                    </label>
                    <button
                      type="button"
                      title="Selecionar no mapa"
                      onClick={() => setShowMapPropriedade(true)}
                      style={{
                        background: '#1976d2',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        padding: '4px 10px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      🗺️ Selecionar no mapa
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: 8, width: '100%', boxSizing: 'border-box' }}>
                    <input
                      value={coordenadaX}
                      onChange={e => setCoordenadaX(e.target.value)}
                      placeholder="Longitude (ex: -43.9345)"
                      type="number"
                      step="any"
                      style={{ flex: 1, minWidth: 0, padding: 10, borderRadius: 8, border: '1px solid #b0c4de', fontSize: 15, boxSizing: 'border-box' }}
                    />
                    <input
                      value={coordenadaY}
                      onChange={e => setCoordenadaY(e.target.value)}
                      placeholder="Latitude (ex: -19.9167)"
                      type="number"
                      step="any"
                      style={{ flex: 1, minWidth: 0, padding: 10, borderRadius: 8, border: '1px solid #b0c4de', fontSize: 15, boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
                {showMapPropriedade && (
                  <MapSelectModal
                    onClose={() => setShowMapPropriedade(false)}
                    onSelect={latlng => {
                      setCoordenadaX(Number(latlng.lng).toFixed(6));
                      setCoordenadaY(Number(latlng.lat).toFixed(6));
                      setShowMapPropriedade(false);
                    }}
                  />
                )}

                <input value={area} onChange={e => setArea(e.target.value)} placeholder="Área total (hectares)" type="number" min={0.01} step="any" required style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #b0c4de', fontSize: 16, boxSizing: 'border-box', background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#e0e0e0' : '#333' }} />
                <input value={proprietario} onChange={e => setProprietario(e.target.value)} placeholder="Nome do Proprietário" style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #b0c4de', fontSize: 16, boxSizing: 'border-box', background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#e0e0e0' : '#333' }} />
                <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} placeholder="Observações adicionais" rows={3} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #b0c4de', fontSize: 16, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#e0e0e0' : '#333' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="props-form-submit"
                    type="submit"
                    disabled={previewMode || salvandoPropriedade}
                    style={{
                      flex: 1,
                      background: editandoId ? '#1976d2' : '#2e9e6e',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '0.7rem 0',
                      minHeight: 44,
                      fontWeight: 700,
                      fontSize: 16,
                      cursor: previewMode || salvandoPropriedade ? 'not-allowed' : 'pointer',
                      opacity: previewMode || salvandoPropriedade ? 0.7 : 1
                    }}
                  >
                    {previewMode ? 'Edição desativada na prévia' : (salvandoPropriedade ? 'Salvando...' : 'Salvar alterações')}
                  </button>
                  <button
                    className="props-form-cancel"
                    type="button"
                    onClick={cancelarEdicaoOuCadastro}
                    style={{
                      flex: 1,
                      background: '#e57373',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '0.7rem 0',
                      minHeight: 44,
                      fontWeight: 700,
                      fontSize: 16,
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </>
        ) : (
          <>
            {/* Lista de propriedades cadastradas */}
            {propriedades.length > 0 && (
              <div className="props-section-heading">
                <div><span>Portfólio territorial</span><h2>Propriedades cadastradas</h2></div>
                <b>{propriedadesFiltradas.length} resultado{propriedadesFiltradas.length !== 1 ? 's' : ''}</b>
              </div>
            )}
            {propriedades.length === 0 ? (
              <section className="props-empty-state" aria-labelledby="props-empty-title">
                <div className="props-empty-main">
                  <span className="props-empty-kicker">Cadastro territorial centralizado</span>
                  <div className="props-empty-icon" aria-hidden="true">🏡</div>
                  <h2 id="props-empty-title">Nenhuma propriedade cadastrada</h2>
                  <p>
                    Desenhe e salve o perímetro da fazenda pela Central de Operações. A propriedade
                    aparecerá aqui automaticamente para consulta, edição e gestão dos talhões.
                  </p>
                  {onNavigate && (
                    <button type="button" className="props-empty-primary" onClick={() => onNavigate('sensoriamento')}>
                      Abrir Central de Operações
                      <FaArrowRight aria-hidden="true" />
                    </button>
                  )}
                  <span className="props-empty-note">O cadastro é feito diretamente sobre o mapa.</span>
                </div>
                <div className="props-empty-benefits" aria-label="Recursos disponíveis após o cadastro">
                  <div className="props-empty-benefit">
                    <span><FaMapMarkerAlt aria-hidden="true" /></span>
                    <div>
                      <strong>Localização e área</strong>
                      <p>Identifique município, estado, coordenadas e tamanho da propriedade.</p>
                    </div>
                  </div>
                  <div className="props-empty-benefit">
                    <span><FaLayerGroup aria-hidden="true" /></span>
                    <div>
                      <strong>Talhões e culturas</strong>
                      <p>Divida a área produtiva e acompanhe cada plantio separadamente.</p>
                    </div>
                  </div>
                  <div className="props-empty-benefit">
                    <span><FaHistory aria-hidden="true" /></span>
                    <div>
                      <strong>Histórico de campo</strong>
                      <p>Centralize eventos, fotos, custos e informações agronômicas.</p>
                    </div>
                  </div>
                </div>
              </section>
            ) : (
              <div className="props-card-grid" style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 16
              }}>
                {propriedadesFiltradas.map(p => {
                  const talhoes = talhoesPorPropriedade[p.id] || [];
                  const talhoesAtivos = talhoes.filter(t => isActiveCrop(t.ciclo_atual || t)).length;
                  const culturas = [...new Set(talhoes.map(t => t.cultura).filter(Boolean).map(c => c.trim()).map(c => c.charAt(0).toUpperCase() + c.slice(1).toLowerCase()))];
                  const permissao = permissoesPorPropriedade[p.id] || 'viewer'; // NOVO
                  const souOwner = permissao === 'owner'; // NOVO
                  const podeEditarCadastro = propertyActionAllowed(p, 'propriedades', 'edit', souOwner || permissao === 'editor');
                  
                  return (
                  <div
                    className="tech-property-card props-property-card"
                    key={p.id}
                    style={{
                      borderRadius: 12,
                      padding: 14,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      border: talhoes.length > 0 ? '2px solid #43cea2' : '1px solid #e0e0e0',
                      position: 'relative',
                      transition: 'all 0.2s ease',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={() => setHoverCards({...hoverCards, [p.id]: true})}
                    onMouseLeave={() => setHoverCards({...hoverCards, [p.id]: false})}
                  >
                    {/* Ações flutuantes DESKTOP (aparecem ao hover, >= 768px) */}
                    {!previewMode && (souOwner || podeEditarCadastro) && (
                      <div style={{
                        position: 'absolute',
                        top: talhoes.length > 0 ? 30 : 8,
                        right: !isMobile && permissao === 'editor' ? 46 : 8,
                        display: isMobile ? 'none' : 'flex',
                        gap: 6,
                        opacity: hoverCards[p.id] ? 1 : 0,
                        transition: 'opacity 0.2s ease',
                        zIndex: 10
                      }}>
                      {/* Editar - owner ou editor */}
                      {podeEditarCadastro && (
                        <button
                          type="button"
                          aria-label={`Editar propriedade ${p.nome}`}
                          style={{
                            background: '#1976d2',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(25, 118, 210, 0.4)',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => iniciarEdicao(p)}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          title="Editar propriedade"
                        >
                          <FaPencilAlt size={12} />
                        </button>
                      )}
                      
                      {/* Compartilhar - apenas owner */}
                      {souOwner && (
                        <button
                          type="button"
                          aria-label={`Compartilhar propriedade ${p.nome}`}
                          style={{
                            background: '#ff9800',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(255, 152, 0, 0.4)',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => setModalCompartilhar({ aberto: true, propriedade: p })}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          title="Compartilhar propriedade"
                        >
                          <FaUserFriends size={12} />
                        </button>
                      )}
                      
                      {/* Excluir - apenas owner */}
                      {souOwner && (
                        <button
                          type="button"
                          aria-label={`Excluir propriedade ${p.nome}`}
                          style={{
                            background: '#e57373',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(229, 115, 115, 0.4)',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => pedirConfirmacaoExclusao(p.id, p.nome)}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          title="Excluir propriedade"
                        >
                          <FaTrash size={12} />
                        </button>
                      )}
                    </div>
                    )}
                    
                    {/* Sair do compartilhamento - apenas viewer/editor (não owner) */}
                    {!previewMode && !souOwner && (
                      <div style={{
                        position: 'absolute',
                        top: talhoes.length > 0 ? 30 : 8,
                        right: 8,
                        display: 'flex',
                        gap: 6,
                        opacity: isMobile ? 1 : (hoverCards[p.id] ? 1 : 0),
                        transition: 'opacity 0.2s ease',
                        zIndex: 10
                      }}>
                        <button
                          type="button"
                          style={{
                            background: '#ff5722',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(255, 87, 34, 0.4)',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => handleRemoverCompartilhamento(p.id)}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          aria-label="Sair da propriedade compartilhada"
                          title="Sair da propriedade compartilhada (não exclui os dados)"
                        >
                          <FaSignOutAlt size={13} />
                        </button>
                      </div>
                    )}
                    
                    {/* Faixa superior do card — permissão + status */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      margin: '-14px -14px 4px -14px',
                      padding: '6px 12px',
                      borderRadius: '12px 12px 0 0',
                      background: (() => {
                        if (!souOwner) return permissao === 'editor' ? '#e3f2fd' : '#f5f5f5';
                        if (talhoes.length === 0) return '#fafafa';
                        return talhoesAtivos > 0 ? '#e8f5e9' : '#fff8e1';
                      })(),
                      borderBottom: '1px solid',
                      borderColor: (() => {
                        if (!souOwner) return permissao === 'editor' ? '#bbdefb' : '#e0e0e0';
                        if (talhoes.length === 0) return '#e0e0e0';
                        return talhoesAtivos > 0 ? '#c8e6c9' : '#ffe082';
                      })()
                    }}>
                      {/* Lado esquerdo: permissão ou proprietario */}
                      {!souOwner ? (
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          color: permissao === 'editor' ? '#1565c0' : '#757575',
                          display: 'flex', alignItems: 'center', gap: 4
                        }}>
                          {permissao === 'editor' ? <><FaEdit size={10} /> Editor</> : <><FaEye size={10} /> Visualizador</>}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: '#aaa' }}>🏡 Minha</span>
                      )}
                      {/* Lado direito: status de talhoes */}
                      {talhoes.length > 0 ? (
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          color: talhoesAtivos > 0 ? '#2e7d32' : '#e65100'
                        }}>
                          {talhoesAtivos > 0
                            ? `✓ ${talhoesAtivos} ativo${talhoesAtivos > 1 ? 's' : ''}`
                            : '○ Sem cultivo ativo'}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: '#bbb' }}>Sem talhões</span>
                      )}
                    </div>

                    <div className="props-card-title" style={{ fontWeight: 800, color: darkMode ? '#90caf9' : '#1976d2', fontSize: 19, display: 'flex', alignItems: 'center', gap: 7, lineHeight: 1.2 }}>
                      <span className="props-card-title-icon"><FaHome aria-hidden="true" /></span>
                      <span>{p.nome}</span>
                    </div>
                    
                    {/* Informações básicas */}
                    <div className="props-card-facts" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 13, color: darkMode ? '#aaa' : '#666' }}>
                      <div><FaMapMarkerAlt aria-hidden="true" /> {p.municipio} - {p.estado}</div>
                      <div><FaRulerCombined aria-hidden="true" /> {p.area || 0} ha</div>
                    </div>
                    
                    {/* Informações de talhões */}
                    {talhoes.length > 0 && (
                      <div style={{
                        background: darkMode ? '#1a2a3a' : '#f0f7ff',
                        borderRadius: 8,
                        padding: 10,
                        marginTop: 4
                      }}>
                        <div className="props-card-plots-title" style={{ fontSize: 13, fontWeight: 600, color: darkMode ? '#90caf9' : '#1976d2', marginBottom: 6 }}>
                          <FaLayerGroup aria-hidden="true" /> {talhoes.length} {talhoes.length > 1 ? 'Talhões' : 'Talhão'}
                        </div>
                        {culturas.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                            {culturas.map(c => (
                              <span key={c} style={{
                                background: '#1976d2',
                                color: '#fff',
                                padding: '2px 8px',
                                borderRadius: 12,
                                fontSize: 11,
                                fontWeight: 600
                              }}>
                                {c}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {p.proprietario && (
                      <div style={{ fontSize: 13, color: darkMode ? '#e0e0e0' : '#555' }}>
                        👤 {p.proprietario}
                      </div>
                    )}
                    
                    {p.observacoes && (
                      <div style={{ fontSize: 12, color: darkMode ? '#aaa' : '#777', fontStyle: 'italic', background: darkMode ? '#2a2a2a' : '#f9f9f9', padding: 8, borderRadius: 6 }}>
                        {p.observacoes}
                      </div>
                    )}
                    
                    {/* Botão principal - Ver Talhões */}
                    <button
                      type="button"
                      className="tech-action-btn"
                      style={{
                        width: '100%',
                        padding: '12px 0',
                        fontWeight: 700,
                        fontSize: 15,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        transition: 'all 0.2s ease',
                        marginTop: 'auto'
                      }}
                      onClick={() => setPropriedadeSelecionada(p)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#267a56';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(46, 158, 110, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#2e9e6e';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <FaListAlt size={14} /> {previewMode ? 'Ver acompanhamento' : 'Abrir talhões'} <FaArrowRight size={12} />
                    </button>
                    
                    {/* Ações MOBILE (sempre visíveis, < 768px) */}
                    {!previewMode && podeEditarCadastro && (
                      <div style={{
                        display: isMobile ? 'grid' : 'none',
                        gridTemplateColumns: souOwner ? '1fr 1fr 1fr' : '1fr',
                        gap: 6,
                        marginTop: 4
                      }}>
                        {/* Editar */}
                        <button
                          type="button"
                          style={{
                            background: '#1976d2',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            padding: '8px',
                            fontWeight: 600,
                            fontSize: 13,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => iniciarEdicao(p)}
                        >
                          <FaPencilAlt size={11} /> Editar
                        </button>
                        
                        {/* Compartilhar - apenas owner */}
                        {souOwner && (
                          <button
                            type="button"
                            aria-label={`Compartilhar propriedade ${p.nome}`}
                            title={`Compartilhar propriedade ${p.nome}`}
                            style={{
                              background: '#ff9800',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 6,
                              padding: '8px',
                              fontWeight: 600,
                              fontSize: 13,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 4,
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => setModalCompartilhar({ aberto: true, propriedade: p })}
                          >
                            <FaUserFriends size={11} />
                          </button>
                        )}
                        
                        {/* Excluir - apenas owner */}
                        {souOwner && (
                          <button
                            type="button"
                            style={{
                              background: '#e57373',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 6,
                              padding: '8px',
                              fontWeight: 600,
                              fontSize: 13,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 4,
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => pedirConfirmacaoExclusao(p.id, p.nome)}
                          >
                            <FaTrash size={11} /> Excluir
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirmação de exclusão */}
      {confirmarExclusao.aberto && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: darkMode ? '#232526' : '#fff',
            borderRadius: 12,
            padding: 24,
            boxShadow: darkMode ? '0 2px 8px #0006' : '0 4px 8px rgba(0, 0, 0, 0.2)',
            maxWidth: 400,
            width: '90%',
            textAlign: 'center'
          }}>
            <h3 style={{ color: darkMode ? '#90caf9' : '#1976d2', marginBottom: 16, fontWeight: 600, fontSize: 18 }}>
              Confirmar Exclusão
            </h3>
            <p style={{ marginBottom: 24, color: darkMode ? '#e0e0e0' : '#333', fontSize: 16 }}>
              Remover a propriedade "<strong>{confirmarExclusao.nome}</strong>" de Propriedades e Talhões? Ela deixará de aparecer na estrutura nova, mas os dados antigos, fotos e históricos permanecerão preservados.
            </p>
            {erroExclusao && (
              <p role="alert" style={{ margin: '-10px 0 18px', padding: 10, borderRadius: 8, color: '#a33c32', background: '#fff0ee', fontSize: 13 }}>
                {erroExclusao}
              </p>
            )}
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
              <button
                onClick={() => handleExcluir(confirmarExclusao.id)}
                disabled={excluindoPropriedade}
                style={{
                  background: '#e57373',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '0.7rem 1.5rem',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: excluindoPropriedade ? 'wait' : 'pointer',
                  opacity: excluindoPropriedade ? 0.7 : 1,
                  flex: 1
                }}
              >
                {excluindoPropriedade ? 'Removendo…' : 'Remover'}
              </button>
              <button
                onClick={fecharConfirmacao}
                disabled={excluindoPropriedade}
                style={{
                  background: '#1976d2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '0.7rem 1.5rem',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: excluindoPropriedade ? 'not-allowed' : 'pointer',
                  opacity: excluindoPropriedade ? 0.7 : 1,
                  flex: 1
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Compartilhamento - NOVO */}
      {modalCompartilhar.aberto && (
        <ModalCompartilhar
          propriedade={modalCompartilhar.propriedade}
          darkMode={darkMode}
          user={user}
          onClose={() => setModalCompartilhar({ aberto: false, propriedade: null })}
          onAtualizar={carregarPropriedades}
        />
      )}

    </div>
  );
}
